// src/services/consumableSpareLogService.js
import { repo } from "../dbservice/repo";
import { ConsumableSpareLogModel } from "../models/ConsumableSpareLogModel";
import { AssetConsumptionModel }   from "../models/AssetConsumptionModel";

class ConsumableSpareLogService {
  constructor() {
    this.logsCollection        = "consumableSpareLogs";
    this.consumptionsCollection = "assetConsumptions";
    this.assetsCollection      = "assets";
  }

  // ── Auth guard ─────────────────────────────────────────────────────────────

  _isAuthorized(currentUser) {
    return !!(currentUser && (currentUser.uid || currentUser.userId ));
  }

  // ── Stock calculation ──────────────────────────────────────────────────────

  _calculateCurrentStock(logs) {
    return logs.reduce((stock, log) => {
      if (log.transactionType === "Receipt") return stock + Number(log.qty || 0);
      if (log.transactionType === "Issue")   return stock - Number(log.qty || 0);
      return stock;
    }, 0);
  }

  // ── Client-side sort helper ────────────────────────────────────────────────
  // Avoids Firestore composite index requirement on (field + createdDate)

  _sortByDateDesc(records) {
    return [...records].sort((a, b) => {
      const dateA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
      const dateB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
      return dateB - dateA;
    });
  }

  // ── Base filters helper ────────────────────────────────────────────────────

  _buildCompanyFilters(currentUser) {
    if (!currentUser.companyId || currentUser.role === "superadmin") return [];
    return [{ field: "companyId", value: currentUser.companyId }];
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RECORD TRANSACTION
  // Records a Receipt (stock in) or Issue (stock out) against a
  // Consumable or Spare Part asset. Validates stock availability,
  // persists the log entry, and updates the asset quantity atomically.
  // ─────────────────────────────────────────────────────────────────────────

  async recordTransaction(currentUser, transactionData) {
    if (!this._isAuthorized(currentUser)) {
      throw new Error("Unauthorized: Cannot record transaction.");
    }

    // ── Build and validate the model ──────────────────────────────────────
    const logModel = new ConsumableSpareLogModel(
      {
        ...transactionData,
        companyId:      currentUser.companyId      || "",
        locationCode:   currentUser.locationCode   || "",
        createdBy:      currentUser.userId,
        createdByEmail: currentUser.email          || "",
        createdDate:    new Date().toISOString(),
      },
      { isCreation: true }
    );

    const validationErrors = logModel.getValidationErrors();
    if (validationErrors.length > 0) {
      throw new Error(validationErrors[0]);
    }

    // ── Fetch existing logs to compute current stock ──────────────────────
    const existingLogs = await this.getLogsByAssetId(
      logModel.assetId,
      currentUser,
      1000
    );
    const currentStock = this._calculateCurrentStock(existingLogs);

    // ── Stock availability check for Issue transactions ───────────────────
    if (logModel.isIssue() && Number(logModel.qty) > currentStock) {
      throw new Error(
        `Insufficient stock. Available: ${currentStock} ${logModel.uom}, ` +
        `Requested: ${Number(logModel.qty)} ${logModel.uom}.`
      );
    }

    // ── Persist the log entry ─────────────────────────────────────────────
    const payload = logModel.toPayload();
    await repo.upsertData(this.logsCollection, logModel.id, payload);

    // ── Update asset quantity ─────────────────────────────────────────────
    const newStock = logModel.isReceipt()
      ? currentStock + Number(logModel.qty)
      : currentStock - Number(logModel.qty);

    const safeNewStock = Math.max(newStock, 0);

    await repo.upsertData(this.assetsCollection, logModel.assetId, {
      quantity:  safeNewStock,
      updatedAt: new Date().toISOString(),
      updatedBy: currentUser.email || "",
    });

    return {
      ...payload,
      stockBefore: currentStock,
      stockAfter:  safeNewStock,
    };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RECORD CONSUMPTION
  // Records a consumption entry in the assetConsumptions collection and
  // deducts the quantity from the asset inventory.
  // ─────────────────────────────────────────────────────────────────────────

  async recordConsumption(currentUser, consumptionData) {
    if (!this._isAuthorized(currentUser)) {
      throw new Error("Unauthorized: Cannot record consumption.");
    }

    // ── Fetch consumable asset to verify stock ────────────────────────────
    const allLogs      = await this.getLogsByAssetId(
      consumptionData.assetId,
      currentUser,
      1000
    );
    const currentStock = this._calculateCurrentStock(allLogs);
    const consumeQty   = Number(consumptionData.quantity);

    if (consumeQty > currentStock) {
      throw new Error(
        `Consumed Quantity (${consumeQty}) exceeds ` +
        `Available Quantity (${currentStock}).`
      );
    }

    // ── Build and validate the consumption model ──────────────────────────
    const consumptionModel = new AssetConsumptionModel(
      {
        ...consumptionData,
        companyId:      currentUser.companyId || "",
        createdBy:      currentUser.userId,
        createdByEmail: currentUser.email     || "",
        createdDate:    new Date().toISOString(),
      },
      { isCreation: true }
    );

    const validationErrors = consumptionModel.getValidationErrors();
    if (validationErrors.length > 0) {
      throw new Error(validationErrors[0]);
    }

    // ── Persist the consumption record ────────────────────────────────────
    const payload = consumptionModel.toPayload();
    await repo.upsertData(
      this.consumptionsCollection,
      consumptionModel.id,
      payload
    );

    // ── Deduct inventory ──────────────────────────────────────────────────
    const newStock     = Math.max(currentStock - consumeQty, 0);
    const now          = new Date().toISOString();

    await repo.upsertData(this.assetsCollection, consumptionData.assetId, {
      quantity:  newStock,
      updatedAt: now,
      updatedBy: currentUser.email || "",
    });

    return {
      ...payload,
      stockBefore: currentStock,
      stockAfter:  newStock,
    };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // GET LOGS BY ASSET ID
  // Fetches all stock transaction logs for a specific asset.
  // No server-side orderBy — sorted client-side to avoid composite index.
  // ─────────────────────────────────────────────────────────────────────────

  async getLogsByAssetId(assetId, currentUser, limitVal = 200) {
    if (!this._isAuthorized(currentUser)) return [];
    if (!assetId) return [];

    try {
      const rawLogs = await repo.fetchData(
        this.logsCollection,
        [{ field: "assetId", value: assetId }],
        [], // no composite index needed
        limitVal
      );

      return this._sortByDateDesc(rawLogs).map(
        (log) => new ConsumableSpareLogModel(log)
      );
    } catch (err) {
      console.error("getLogsByAssetId failed:", err.message);
      return [];
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // GET ALL LOGS
  // Fetches all stock transaction logs scoped to the current user's company.
  // Supports optional filters: transactionType, assetType, equipmentAssetId.
  // ─────────────────────────────────────────────────────────────────────────

  async getAllLogs(currentUser, filters = {}, limitVal = 500) {
    if (!this._isAuthorized(currentUser)) return [];

    try {
      const baseFilters = this._buildCompanyFilters(currentUser);

      if (filters.transactionType) {
        baseFilters.push({
          field: "transactionType",
          value: filters.transactionType,
        });
      }

      if (filters.assetType) {
        baseFilters.push({
          field: "assetType",
          value: filters.assetType,
        });
      }

      if (filters.equipmentAssetId) {
        baseFilters.push({
          field: "equipmentAssetId",
          value: filters.equipmentAssetId,
        });
      }

      if (filters.assetId) {
        baseFilters.push({
          field: "assetId",
          value: filters.assetId,
        });
      }

      const rawLogs = await repo.fetchData(
        this.logsCollection,
        baseFilters,
        [], // no composite index needed
        limitVal
      );

      return this._sortByDateDesc(rawLogs).map(
        (log) => new ConsumableSpareLogModel(log)
      );
    } catch (err) {
      console.error("getAllLogs failed:", err.message);
      return [];
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // GET CONSUMPTION HISTORY
  // Fetches all consumption records scoped to the current user's company.
  // Supports optional filters: consumedAgainstAssetId, assetType.
  // ─────────────────────────────────────────────────────────────────────────

  async getConsumptionHistory(currentUser, filters = {}, limitVal = 200) {
    if (!this._isAuthorized(currentUser)) return [];

    try {
      const baseFilters = this._buildCompanyFilters(currentUser);

      if (filters.consumedAgainstAssetId) {
        baseFilters.push({
          field: "consumedAgainstAssetId",
          value: filters.consumedAgainstAssetId,
        });
      }

      if (filters.assetType) {
        baseFilters.push({
          field: "assetType",
          value: filters.assetType,
        });
      }

      if (filters.assetId) {
        baseFilters.push({
          field: "assetId",
          value: filters.assetId,
        });
      }

      const rawData = await repo.fetchData(
        this.consumptionsCollection,
        baseFilters,
        [], // no composite index needed — sorted client-side
        limitVal
      );

      return this._sortByDateDesc(rawData).map(
        (record) => new AssetConsumptionModel(record)
      );
    } catch (err) {
      console.error("getConsumptionHistory failed:", err.message);
      return [];
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // GET CURRENT STOCK
  // Computes the current stock level for an asset by replaying all
  // Receipt and Issue transactions from its log history.
  // ─────────────────────────────────────────────────────────────────────────

  async getCurrentStock(assetId, currentUser) {
    if (!this._isAuthorized(currentUser)) return 0;
    if (!assetId) return 0;

    const logs = await this.getLogsByAssetId(assetId, currentUser, 1000);
    return this._calculateCurrentStock(logs);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // GET STOCK SUMMARY
  // Returns a summary object with current stock, total received,
  // total issued, and transaction count for a given asset.
  // ─────────────────────────────────────────────────────────────────────────

  async getStockSummary(assetId, currentUser) {
    if (!this._isAuthorized(currentUser)) {
      return {
        currentStock:     0,
        totalReceived:    0,
        totalIssued:      0,
        transactionCount: 0,
      };
    }

    const logs = await this.getLogsByAssetId(assetId, currentUser, 1000);

    const totalReceived = logs
      .filter((l) => l.transactionType === "Receipt")
      .reduce((sum, l) => sum + Number(l.qty || 0), 0);

    const totalIssued = logs
      .filter((l) => l.transactionType === "Issue")
      .reduce((sum, l) => sum + Number(l.qty || 0), 0);

    return {
      currentStock:     Math.max(totalReceived - totalIssued, 0),
      totalReceived,
      totalIssued,
      transactionCount: logs.length,
    };
  }
}

export const consumableSpareLogService = new ConsumableSpareLogService();